import { isValidEmail, isValidPhoneNumber } from '@/utils/formHelpers';

type CustomPropertyRecord = { [key: string]: string | number };

export type TelemetryType =
  | 'RemoteDependencyData'
  | 'EventData'
  | 'ExceptionData'
  | 'MessageData'
  | 'MetricData'
  | 'PageViewData'
  | 'PageViewData'
  | 'RequestData';

export const telemetryTypesToModify: TelemetryType[] = ['RemoteDependencyData'];

/* This regex matches either:
 * 1. A UUID/GUID (8-4-4-4-12 hex format) that is immediately preceded by a slash ("/").
 * 2. A number (integer or decimal) that is immediately preceded by a slash ("/") and not preceded by a letter or digit (using negative lookbehind),
 *      and is followed by either a slash or the end of the string (using positive lookahead). */
const regex: RegExp =
  /(?<=\/)([a-fA-F0-9]{8}-(?:[a-fA-F0-9]{4}-){3}[a-fA-F0-9]{12}|(?<![a-zA-Z0-9])(\d+(?:\.\d+)?)(?=\/|\?|$))/g;

const hasExcludedData = (value: string | number) => {
  return isValidEmail(value as string) || isValidPhoneNumber(value as string);
};

/**
 * Replaces numeric IDs and UUIDs in a URL path with numbered placeholders (e.g., $0, $1, ...).
 * @param url - The URL string to process
 * @returns {string} URL string with numeric IDs and UUIDs replaced by placeholders
 */
export const replaceWithPlaceholders = (str: string): string => {
  let counter = 0;
  return str.replace(regex, _ => `$${counter++}`);
};

/**
 * Removes query string from a URL
 * @param url - The URL string to process
 * @returns {string} URL string with query parameters removed
 */
export const removeQueryString = (url: string): string => {
  const questionMarkIndex = url.indexOf('?');
  return questionMarkIndex >= 0 ? url.substring(0, questionMarkIndex) : url;
};

/**
 * Converts the query string of a URL into a Map of key-value pairs.
 * @param url - A string representing a valid URL
 * @returns A map where each key is a query parameter name and each value is the parameter value (as string or number)
 */
export const convertQueryStringToMap = (url: string): CustomPropertyRecord => {
  try {
    const _u = new URL(url);

    const result: CustomPropertyRecord = {};
    for (const q of _u.searchParams) {
      if (result[q[0] as string]) continue;

      if (hasExcludedData(q[1])) {
        result[q[0] as string] = '$masked';
      } else {
        result[q[0] as string] = q[1] ?? 'undefined';
      }
    }

    return result;
  } catch {
    return {};
  }
};
